import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

// The design system defines custom `ds` scales (text-ds-*, rounded-ds-*,
// shadow-ds-*). Default tailwind-merge doesn't know them, so it can't dedupe a
// base class against an override on the same property — it keeps BOTH and CSS
// source-order decides, e.g. a base `rounded-ds-md` beating an override
// `rounded-ds-full`, or a `text-ds-sm` size stripping a `text-ds-primary-foreground`
// color. Register the ds scales so each property merges correctly (last wins).
// Matches every step of the ds spacing scale (ds-0, ds-px, ds-0.5 … ds-24)
// without enumerating it — the scale's shape, not its members, is the contract.
const isDsSpacing = (value: string) => /^ds-(?:px|\d+(?:\.\d+)?)$/.test(value)

const twMerge = extendTailwindMerge({
	extend: {
		classGroups: {
			'font-size': [
				{
					text: [
						'ds-4xs',
						'ds-3xs',
						'ds-2xs',
						'ds-xs',
						'ds-sm',
						'ds-base',
						'ds-lg',
						'ds-xl',
						'ds-2xl',
						'ds-3xl',
						'ds-4xl',
						'ds-5xl',
					],
				},
			],
			p: [{ p: [isDsSpacing] }],
			px: [{ px: [isDsSpacing] }],
			py: [{ py: [isDsSpacing] }],
			pt: [{ pt: [isDsSpacing] }],
			pr: [{ pr: [isDsSpacing] }],
			pb: [{ pb: [isDsSpacing] }],
			pl: [{ pl: [isDsSpacing] }],
			m: [{ m: [isDsSpacing] }],
			mx: [{ mx: [isDsSpacing] }],
			my: [{ my: [isDsSpacing] }],
			mt: [{ mt: [isDsSpacing] }],
			mr: [{ mr: [isDsSpacing] }],
			mb: [{ mb: [isDsSpacing] }],
			ml: [{ ml: [isDsSpacing] }],
			gap: [{ gap: [isDsSpacing] }],
			'gap-x': [{ 'gap-x': [isDsSpacing] }],
			'gap-y': [{ 'gap-y': [isDsSpacing] }],
			rounded: [
				{
					rounded: [
						'ds-none',
						'ds-sm',
						'ds-base',
						'ds-md',
						'ds-lg',
						'ds-xl',
						'ds-2xl',
						'ds-full',
						'ds-card',
						'ds-modal',
						'ds-control',
					],
				},
			],
			tracking: [{ tracking: ['ds-wide', 'ds-wider', 'ds-widest'] }],
			shadow: [{ shadow: ['ds-sm', 'ds-base', 'ds-md', 'ds-lg', 'ds-xl', 'ds-2xl'] }],
		},
	},
})

/**
 * Merge class names, resolving conflicting Tailwind utilities so the last one wins.
 * The single class-composition helper shared by every `@atlas/ui-*` package.
 */
export function cn(...inputs: ClassValue[]): string {
	return twMerge(clsx(inputs))
}
